// src/services/imageService.js - Complete Fix for disappearing images
//import axios from 'axios';
import proxyapi from './ProxyApi';
import { proxyImageUrl } from '../config/api';

/**
 * Service for handling image-related operations
 */

/**
 * Save image mappings and update HTML with images
 * 
 * @param {string} viewId - The view ID
 * @param {string} versionId - The version ID
 * @param {Object} uploadedImages - Object mapping placeholder IDs to image objects
 * @param {string} userId - Current user ID
 * @returns {Promise<Object>} - The response with updated HTML
 */
export const saveImageMappings = async (viewId, versionId, uploadedImages, userId) => {
  try {
    // Handle both shapes: string or { imageId }
    const flat = Object.entries(uploadedImages).reduce((acc, [ph, obj]) => {
      if (!obj) return acc;
      if (typeof obj === "string") acc[ph] = obj;          // already flat
      else if (obj.imageId)        acc[ph] = obj.imageId;  // wrapped form
      return acc;
    }, {});

    const response = await proxyapi.put(
      `/api/v1/views/${viewId}/versions/${versionId}/images`,
      { 
        userId,
        imageMappings: flat
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error saving image mappings:', error);
    throw error;
  }
};

/**
 * Update the HTML content with image URLs for preview only
 * This is a frontend-only solution for immediate feedback
 * 
 * ROOT CAUSE FIXED: The issue was that we were processing and serializing the entire document,
 * causing special styles like the SVG mask-image to be corrupted.
 * This new version processes elements individually and avoids serializing the entire document.
 * 
 * @param {string} html - Original HTML content
 * @param {Object} uploadedImages - Mapping of image IDs to image metadata
 * @param {string} userId - Current user ID for proxy URLs
 * @returns {string} - Updated HTML content
 */
// src/services/imageService.js

export const updateHtmlWithImages = (html, uploadedImages, userId) => {
  if (!uploadedImages || !html || !userId) return html;

  try {
    // Parse the HTML as a DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // --- 1. Update static HTML placeholders as before ---
    for (const [phId, data] of Object.entries(uploadedImages)) {
      const imageId = typeof data === "string" ? data : data.imageId;
      if (!imageId) continue;
      const placeholder = doc.getElementById(phId);
      if (!placeholder) continue;
      const imageUrl = proxyImageUrl(imageId, userId); // replaced
      let imgElement = placeholder.querySelector('img');
      if (imgElement) {
        imgElement.src = imageUrl;
        imgElement.alt = data.fileName || "Image";
      } else {
        placeholder.innerHTML = "";
        imgElement = document.createElement("img");
        imgElement.src = imageUrl;
        imgElement.alt = data.fileName || "Image";
        imgElement.className = "w-full h-full object-cover";
        placeholder.appendChild(imgElement);
      }
      const match = phId.match(/[0-9]+/);
      if (match) {
        placeholder.setAttribute('data-number', match[0]);
      }
    }

    // --- 2. Update image placeholders inside <script> tags (dynamic injection) ---
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => {
      let scriptCode = script.textContent;
      let scriptCodeUpdated = scriptCode;

      for (const [phId, data] of Object.entries(uploadedImages)) {
        const imageId = typeof data === "string" ? data : data.imageId;
        if (!imageId) continue;
        const imageUrl = proxyImageUrl(imageId, userId); // replaced

        // Build a regex to match the placeholder div for this id
        // e.g., <div ... id="image-1" ...> ... </div>
        const divRegex = new RegExp(
          `<div([^>]*?)id=["']${phId}["']([^>]*?)>([\\s\\S]*?)<\\/div>`,
          'g'
        );

        // Replacement: <div ... id="image-X" ...><img ... /></div>
        scriptCodeUpdated = scriptCodeUpdated.replace(divRegex, (match, beforeId, afterId, inner) => {
          const imgTag = `<img src="${imageUrl}" alt="${data.fileName || 'Image'}" class="w-full h-full object-cover" />`;
          return `<div${beforeId}id="${phId}"${afterId}>${imgTag}</div>`;
        });
      }

      // Only update script content if it changed
      if (scriptCode !== scriptCodeUpdated) {
        script.textContent = scriptCodeUpdated;
      }
    });

    // Return the updated HTML
    return doc.documentElement.outerHTML;
  } catch (error) {
    console.error('Error updating HTML with images:', error);
    return html;
  }
};